import {Component, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {NavComponent} from '../../../components/nav/nav.component';
import {ApiService} from '../../../shared/services/api.service';
import {IdentityMessage, ServiceMessage} from '../../../shared/models/message';
import {ServiceInterface} from '../../../shared/models/services';
import {ClaimType, ScanShareData} from '../../../shared/models/claim';
import {Subscription} from 'rxjs';
import {SyncService} from 'src/app/shared/services/sync.service';
import {LoaderService, NavigateService} from '@satoris/ngx-satoris';
import {ShareHandlerService} from 'src/app/shared/services/share-handler.service';

@Component({
  selector: 'app-service-iframe',
  templateUrl: './service-iframe.component.html',
  styleUrls: ['./service-iframe.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ServiceIframeComponent implements OnInit, OnDestroy {
  service: ServiceInterface;
  private subscription: Subscription;
  constructor(private route: ActivatedRoute,
              private api: ApiService,
              private router: Router,
              private sync: SyncService,
              private loader: LoaderService,
              private nav: NavigateService,
              private shareHandler: ShareHandlerService) {
    this.data = <any> this.router.getCurrentNavigation()?.extras?.state;
  }
  data: {
    fromRoute: string
  };

  private boundHandleIframeMessage: (event: MessageEvent) => void;

  ngOnInit() {
    NavComponent.prototype.backToButton = this.data?.fromRoute ? this.data?.fromRoute  : 'services';
    this.route.queryParams.subscribe(params => {
      if(params?.service) {
        this.service = JSON.parse(params.service);
        this.api.trackAnalytics('service', this.service.title).catch(() => {});
      }
    });
    this.boundHandleIframeMessage = this.handleIframeMessage.bind(this);
    window.addEventListener('message', this.boundHandleIframeMessage);
    this.subscription = this.sync.isOnline$.subscribe(isOnline => {
      if(!isOnline){
        this.loader.loading(true);
        setTimeout(() => this.loaderOffline(), 2000);
      } else {
        this.loaderOffline();
      }
    });
  }

  loaderOffline() {
    if(!this.sync.isOnline) {
      this.loader.loading(true, {type: 'error', message: 'iframe.offline', custom: {icon: 'wifi', closeBtnLabel: 'global.back'}}).then(() => !this.sync.isOnline && this.loader.loading(true, {type: 'warn', message: 'iframe.ask.return', btnLabel: 'global.confirm'}).then((valid: boolean) => valid ? this.nav.to('user') : this.loaderOffline()));
    } else {
      this.loader.loading(false);
    }
  }

  ngOnDestroy() {
    NavComponent.prototype.backToButton = undefined;
    this.subscription.unsubscribe();
    if(this.boundHandleIframeMessage) {
      window.removeEventListener('message', this.boundHandleIframeMessage);
    }
    if(this.service) this.api.trackAnalytics('service-disconnect', this.service.title).catch(() => {});
  }

  loaded() {
    if(this.service.supportsSignedShare) {
      this.sendMessageToIframe('APP_LOADED');
    }
    this.service.message.forEach(message => {
      switch(message) {
        case ServiceMessage.IDENTITY:
          this.identity();
          break;
        case ServiceMessage.DISABLE_MENU:
          this.disableMenu();
          break;
      }
    });
  }

  handleIframeMessage(event: MessageEvent) {
    // Allow messages from the app's own origin AND from the loaded service's origin
    const isAppOrigin = event.origin === window.location.origin;
    const isServiceOrigin = this.service?.url && event.origin === new URL(this.service.url).origin;
    if(!isAppOrigin && !isServiceOrigin) {
      return;
    }
    if(event.data && event.data.type === 'REQUEST_SHARE') {
      const scanShareData: ScanShareData = event.data.scanShareData;
      this.shareHandler.handleShareRequest(scanShareData).then(success => {
        if(!success) {
          // If sharing failed or user declined, trigger fallback with IDENTITY message
          this.sendShareDeclined(scanShareData.requestId);
          // Also send the unsigned identity data for pre-filling
          this.identity();
        }
      });
    }
  }

  sendShareDeclined(requestId: string) {
    const iframe = document.getElementById('service-iframe') as HTMLIFrameElement;
    if(iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage({
        type: 'SHARE_DECLINED',
        requestId: requestId
      }, '*');
    }
  }

  sendMessageToIframe(type: string, data?: any) {
    const iframe = document.getElementById('service-iframe') as HTMLIFrameElement;
    if(iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage({ type, data }, '*');
    }
  }

  identity() {
    const data: IdentityMessage = {
      nni: this.api.userInfo.nationalNumber,
      can: this.api.claims[ClaimType.ID_CARD].can,
      date_of_birth: new Date(this.api.claims[ClaimType.ID_CARD].date_of_birth),
      doc_nr: this.api.claims[ClaimType.ID_CARD].doc_nr,
      expiry_date: new Date(this.api.claims[ClaimType.ID_CARD].expiry_date),
      first_name: this.api.claims[ClaimType.ID_CARD].first_name,
      gender: this.api.claims[ClaimType.ID_CARD].gender,
      height: this.api.claims[ClaimType.ID_CARD].height,
      image_url: this.api.claims[ClaimType.ID_CARD].image_url,
      last_name: this.api.claims[ClaimType.ID_CARD].last_name,
      middle_name: this.api.claims[ClaimType.ID_CARD].middle_name,
      mrz: this.api.claims[ClaimType.ID_CARD].mrz,
      nationality: this.api.claims[ClaimType.ID_CARD].nationality,
      place_of_birth: this.api.claims[ClaimType.ID_CARD].place_of_birth,
      profession: this.api.claims[ClaimType.ID_CARD].profession,
      signature: this.api.claims[ClaimType.ID_CARD].signature
    }; //TODO replace when MR is ready with new Claims variable
    (document.getElementById('service-iframe') as HTMLIFrameElement).contentWindow.postMessage({type: ServiceMessage.IDENTITY, data}, '*');
  }

  disableMenu() {
    (document.getElementById('service-iframe') as HTMLIFrameElement).contentWindow.postMessage({type: ServiceMessage.DISABLE_MENU}, '*');
  }
}
